import type {
  ApiSuccess,
  ChatMessageItem,
  ChatResponse,
  LotteryChatHelp,
  ChatSessionSummary,
  ChatTurn,
  ListChatMessagesQuery,
  ListChatSessionsQuery,
} from '@todo/shared';
import { api, del, get, getPaged, patch } from './client';

export type { ChatTurn };

export interface ChatHelpModule {
  module: string;
  description: string;
  examples: string[];
}

export interface ChatHelp {
  enabled: boolean;
  modules: ChatHelpModule[];
  /** Whether this person's chat answers lottery questions (display only - the server gates every message). */
  lottery?: LotteryChatHelp;
}

/**
 * Any message may be routed to the Lottery analyst (up to 100 s on the
 * server, after the routing round), and the widget cannot know which
 * beforehand - so every message waits this long.
 */
export const CHAT_TIMEOUT_MS = 120_000;

export const chatApi = {
  /**
   * One message. The SERVER keeps the conversation: send the session id
   * (null starts a new session, whose id comes back).
   *
   * `history` is sent ONLY in stateless mode - when the server could not
   * store the chat (its tables are not installed yet) and answered without
   * a sessionId. Then the widget replays its recent turns, as it did before
   * sessions existed. An empty history is left out, so the server still
   * tries to start a stored session first.
   *
   * ONE chat (CHATBOT_DESIGN.md 15): no mode and no persona - the server
   * decides whether a message is a lottery question and routes it.
   *
   * The agent can make several model round trips - give it longer than
   * the client's default 20s. `changed` means a write tool ran - the widget
   * broadcasts it so open pages refetch.
   */
  async send(
    message: string,
    sessionId: string | null,
    history: ChatTurn[] = [],
  ): Promise<ChatResponse> {
    const res = await api.post<ApiSuccess<ChatResponse>>(
      '/chat',
      {
        message,
        ...(sessionId ? { sessionId } : {}),
        ...(!sessionId && history.length > 0 ? { history } : {}),
      },
      { timeout: CHAT_TIMEOUT_MS },
    );
    return {
      reply: res.data.data.reply,
      changed: res.data.data.changed === true,
      sessionId: res.data.data.sessionId ?? null,
      payload: res.data.data.payload ?? null,
    };
  },

  help(): Promise<ChatHelp> {
    return get<ChatHelp>('/chat/help');
  },

  /** GET /api/chat/sessions - newest activity first, with `meta`. */
  sessions(query: ListChatSessionsQuery = {}): Promise<ApiSuccess<ChatSessionSummary[]>> {
    return getPaged<ChatSessionSummary[]>('/chat/sessions', query as Record<string, unknown>);
  },

  /** GET /api/chat/sessions/:id/messages - page 1 is the most recent turns, oldest first. */
  messages(id: string, query: ListChatMessagesQuery = {}): Promise<ApiSuccess<ChatMessageItem[]>> {
    return getPaged<ChatMessageItem[]>(
      `/chat/sessions/${encodeURIComponent(id)}/messages`,
      query as Record<string, unknown>,
    );
  },

  rename(id: string, title: string): Promise<ChatSessionSummary> {
    return patch<ChatSessionSummary>(`/chat/sessions/${encodeURIComponent(id)}`, { title });
  },

  remove(id: string): Promise<void> {
    return del(`/chat/sessions/${encodeURIComponent(id)}`);
  },
};
